import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";
import HtmlEmbed from "@/components/HtmlEmbed";
import api from "@/lib/api";

/**
 * Public-facing feedback / bug-report page.
 *
 * Mirrors the Invest + BetaSignup pattern: the Cognito form HTML lives
 * in `marketing_settings.feedback_embed` so admins can paste/update it
 * from the marketing-settings admin page without a deploy. If the
 * field is empty we render a graceful fallback that points at the
 * contact page so the URL is never a dead end.
 */
export default function Feedback() {
  const [embed, setEmbed] = useState("");
  useEffect(() => {
    api.get("/marketing/settings")
      .then((r) => setEmbed(r.data?.feedback_embed || ""))
      .catch(() => setEmbed(""));
  }, []);

  return (
    <div className="bg-[#fbfcfc] min-h-screen">
      <MarketingNav />
      <section className="bloq-section">
        <div className="bloq-container">
          <div className="max-w-2xl mb-12">
            <p className="bloq-eyebrow">Feedback &amp; bug reports</p>
            <h1 className="bloq-display text-5xl sm:text-6xl mt-3 mb-5">
              How are we doing? <span className="bloq-gradient-text">Help us make Bloq better.</span>
            </h1>
            <p className="text-lg text-[#4a4a52] leading-relaxed">
              Whether it's a feature you love, something that could be smoother, or a bug
              that's slowing you down — drop a line below. We read every submission.
            </p>
          </div>

          {/* Cognito form embed — falls back to a contact CTA when empty. */}
          <div className="bloq-glass-strong p-10 rounded-2xl" data-testid="feedback-embed-card">
            <p className="bloq-eyebrow">Share your feedback</p>
            <h2 className="bloq-headline text-3xl mt-3 max-w-xl mb-4">
              Tell us what's working, what's not, or what you'd love to see next.
            </h2>
            <p className="text-[#4a4a52] mb-8 max-w-xl">
              Every submission lands in front of our team. We read each one and reply within 3 business
              days when a response is needed.
            </p>
            {embed ? (
              <HtmlEmbed
                html={embed}
                className="bloq-cognito-embed"
                testId="feedback-cognito-embed"
              />
            ) : (
              <div className="border-2 border-dashed border-[#005e7a]/30 rounded-xl p-8 text-center bg-white/50">
                <p className="text-sm text-[#82828c] mb-4">
                  The feedback form is coming online shortly.
                </p>
                <Link
                  to="/contact"
                  className="bloq-btn-primary inline-flex items-center gap-2"
                  data-testid="feedback-contact-fallback"
                >
                  Contact us directly <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
      <MarketingFooter />
    </div>
  );
}
